import {SelectedMediaDevices, WebClientGrant} from "@/types";
import {UserProfile} from "oidc-client-ts";
import {useCallback, useSyncExternalStore} from "react";

const LOCAL_STORAGE_UPDATE_EVENT = "concerto_storage_update";
const CROSS_TAB_STORAGE_EVENT = "storage";

export interface LocalStorageSchema {
    profile: UserProfile | null;
    beforeLoginUrl: string | null;
    webClientGrant: WebClientGrant;
    selectedMediaDevices: SelectedMediaDevices | null;
}

const LOCAL_STORAGE_DEFAULTS: LocalStorageSchema = {
    profile: null,
    beforeLoginUrl: null,
    webClientGrant: "ask",
    selectedMediaDevices: null,
} as const;

let snapshotCache: LocalStorageSchema = {...LOCAL_STORAGE_DEFAULTS};

/**
 * Type-safe wrapper around localStorage.
 */
export const concertoLocalStorage = {
    /**
     * Get an item from localStorage, with a default value if it doesn't exist.
     */
    get: <K extends keyof LocalStorageSchema>(key: K): LocalStorageSchema[K] => {
        const raw = window.localStorage.getItem(key);
        if (raw === null) return LOCAL_STORAGE_DEFAULTS[key];

        try {
            return JSON.parse(raw) as LocalStorageSchema[K];
        } catch (e) {
            console.error(`Could not parse ${key} from localStorage:`, e);
            return LOCAL_STORAGE_DEFAULTS[key];
        }
    },

    set: <K extends keyof LocalStorageSchema>(key: K, value: LocalStorageSchema[K]) => {
        window.localStorage.setItem(key, JSON.stringify(value));
        window.dispatchEvent(new CustomEvent(LOCAL_STORAGE_UPDATE_EVENT, {detail: key}));
    },

    getSnapshot: (): LocalStorageSchema => snapshotCache,

    updateSnapshot: () => {
        snapshotCache = {
            profile: concertoLocalStorage.get("profile"),
            beforeLoginUrl: concertoLocalStorage.get("beforeLoginUrl"),
            webClientGrant: concertoLocalStorage.get("webClientGrant"),
            selectedMediaDevices: concertoLocalStorage.get("selectedMediaDevices"),
        };
    },
};

concertoLocalStorage.updateSnapshot(); // initialize the snapshot cache on load

/**
 * A hook to subscribe to changes in the concertoLocalStorage. It will re-render the component whenever the selected value changes.
 *
 * This hook does not provide any way to update the local storage. In order to do that, @see concertoLocalStorage.set
 *
 * @param selector A function that selects a specific value from the local storage schema to watch for changes.
 * @returns The current value of the selected item from local storage, which will update whenever it changes.
 */
export function useWatchConcertoStorage<T>(selector: (state: LocalStorageSchema) => T): T {
    const subscribe = useCallback((callback: () => void) => {
        const handleUpdate = () => {
            concertoLocalStorage.updateSnapshot();
            callback();
        };

        // we will listen to changes made by the same tab
        window.addEventListener(LOCAL_STORAGE_UPDATE_EVENT, handleUpdate);

        // as well as any other tabs open
        window.addEventListener(CROSS_TAB_STORAGE_EVENT, handleUpdate);

        // cleanup
        return () => {
            window.removeEventListener(LOCAL_STORAGE_UPDATE_EVENT, handleUpdate);
            window.removeEventListener(CROSS_TAB_STORAGE_EVENT, handleUpdate);
        };
    }, []);

    // useSyncExternalStore will ensure the watcher will get up to date values
    return useSyncExternalStore(subscribe, () => selector(concertoLocalStorage.getSnapshot()));
}
